import { useState } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { track } from 'shared/analytics/track'
import { showNotificationToast } from 'shared/utils/notify'
import {
  connectSelectedSolanaWallet,
  getWalletErrorMessage,
  isUserRejectedWalletError
} from '../utils/connect-solana-adapter'
import type { SolanaConnectorProvider } from '../utils/get-solana-connector-providers'

/**
 * Connects a wallet app for a signed-in user (FE-306): wallet-adapter only,
 * no signature. Whether the wallet may play is then the play-wallet model's
 * call (linked and selected, FE-309). Each attempt is reported as
 * `wallet_connect_started` (FE-321).
 */
export const useConnectWalletApp = () => {
  const { wallets, select } = useWallet()
  const [isConnecting, setIsConnecting] = useState(false)

  // eslint-disable-next-line max-statements
  const connectApp = async (provider: SolanaConnectorProvider) => {
    if (isConnecting) return false

    track('wallet_connect_started', { wallet: provider.walletName })
    setIsConnecting(true)
    try {
      await connectSelectedSolanaWallet(wallets, provider.walletName, select)
      return true
    } catch (error) {
      if (!isUserRejectedWalletError(error)) {
        showNotificationToast({
          type: 'error',
          title: 'Wallet was not connected',
          description: getWalletErrorMessage(error)
        })
      }
      return false
    } finally {
      setIsConnecting(false)
    }
  }

  return { connectApp, isConnecting }
}
